import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { removeDownloadFileEverywhere } from '../../ducks/download-panel/download-panel';
import { useMEMTranslation } from '../../utils/text-utils';
import { useTrackEvent } from '../../services/logging/hooks';
import { usePageContext } from '../../utils/page-context';
import { Button, FlyInPanel, CommandBar, Icon } from '../../providers/harmony-scope/lazy-enablers';
import { CustomMEMTable } from '../../shared-components/custom-mem-table';
import { useDownloadPanelColumns, useDownloadStatusLabel, useDownloadPanelRows } from './hooks';
import { useAppSelector } from '../../ducks/hooks';

interface IProps {
  closePanel: () => void;
}

const componentName = 'Download Panel';

export const DownloadsPanel = (props: IProps) => {
  // Redux dispatch
  const dispatch = useDispatch();
  const downloads = useAppSelector((state) => state.downloadManager.downloadFiles);
  const downloadsQueue = useAppSelector((state) => state.downloadManager.downloadFilesQueue);
  const getStatusLabel = useDownloadStatusLabel();

  // Track selected rows for enabling/disabling Delete button
  const [selectedRows, setSelectedRows] = useState<Array<string>>([]);
  const trackEvent = useTrackEvent();
  // Hooks
  const { t } = useMEMTranslation();
  const pageName = usePageContext();
  const rows = useDownloadPanelRows(downloadsQueue, downloads, getStatusLabel);
  const columns = useDownloadPanelColumns();
  // Harmony DataGrid selection event: selectedRow = { id, selected, cells: { fileName }, ... }
  const handleRowSelectChange = useCallback(
    (selectedRow: any) => {
      const {
        id,
        selected,
        cells: { fileName },
      } = selectedRow;
      setSelectedRows((prev) => {
        if (selected) {
          trackEvent('Downloads Panel select ' + fileName, pageName, componentName);
          return prev.includes(id) ? prev : [...prev, id];
        } else {
          trackEvent('Downloads Panel deselect ' + fileName, pageName, componentName);
          return prev.filter((item) => item !== id);
        }
      });
    },
    [trackEvent, pageName]
  );

  // Delete selected downloads everywhere
  const handleDelete = useCallback(
    (selectedIds: Array<string>) => {
      dispatch(removeDownloadFileEverywhere(selectedIds));
      // Not necessary track the filename, because the handleRowSelectChange already does that
      trackEvent('Downloads Panel delete', pageName, componentName);
      // Remove deleted ids from selectedRows
      setSelectedRows((prev) => prev.filter((id) => !selectedIds.includes(id)));
    },
    [dispatch, trackEvent, pageName]
  );
  /* Note: isPanelOpen will always be true since control of the open state is actually in its parent
   **       The state of "opening" here is to control the transition from closed to open since otherwise it would flick open.
   */
  const [isPanelOpen, setPanelOpen] = useState<boolean>(false);
  // Open panel gracefully
  useEffect(() => {
    setPanelOpen(true);
  }, []);
  const { closePanel: parentClosePanel } = props;
  // delay to call parent's close panel to allow css transition to play. lookup info on javascript event loop for more info.
  const closePanel = useCallback(() => {
    setTimeout(parentClosePanel, 0);
  }, [parentClosePanel]);
  const handlePanelClosing = useCallback(() => {
    trackEvent('Downloads panel closed', pageName, componentName);
    closePanel();
  }, [closePanel, pageName, trackEvent]);

  // Effects
  // Track event when the panel opens
  useEffect(() => {
    if (isPanelOpen) {
      trackEvent('Downloads panel opened', pageName, componentName);
    }
  }, [isPanelOpen, trackEvent, pageName]);

  return (
    <FlyInPanel
      heading={t('downloadPanelDownloadsHeading')}
      style={{ '--size': 'var(--he-panel-size-medium)', zIndex: 3 }}
      open={isPanelOpen}
      onHeFlyInAfterHide={handlePanelClosing}
      slot="panel"
      contained
    >
      <CustomMEMTable
        componentName={'DownloadPanels'}
        rowsData={rows}
        columnsInfo={columns}
        comandBar={
          <CommandBar style={{ flex: 2 }}>
            <Button appearance="command" disabled={selectedRows.length === 0} onClick={() => handleDelete(selectedRows)}>
              <Icon slot="start" name="delete"></Icon>
              {t('downloadPanelDelete')}
            </Button>
          </CommandBar>
        }
        rowSelectChange={handleRowSelectChange}
        selectType="multiple"
        selectOnClick={true}
        showFilterBox={false}
      />
      <Button slot="footer" appearance="secondary" onClick={() => setPanelOpen(false)}>
        {t('notificationsPanelClose')}
      </Button>
    </FlyInPanel>
  );
};
